import type {IframeGridPlacement} from './CasinoMarketingBlocksTypes';
import {CASINO_GAMES_GRID_ATTR, CASINO_GAMES_IFRAME_ATTR, MARKETING_BLOCK_KEY_ATTR} from './CasinoMarketingUtils';

const CASINO_GAMES_GRID_SELECTOR = `[${CASINO_GAMES_GRID_ATTR}]`;
const MARKETING_BLOCK_SELECTOR = '.marketing-block';

const POSITION_TOLERANCE_PX = 2;
const SPAN_PREFIX = 'span ';
const DEFAULT_ROW_SPAN = 1;

export const DEFAULT_IFRAME_GRID_PLACEMENT: IframeGridPlacement = {
    rowStart: 1,
    skipShiftBlockKeys: []
};

const lineNum = (v: string): number | null => {
    if (!v || v === 'auto' || v.startsWith(SPAN_PREFIX)) {
        return null;
    }

    const n = parseInt(v, 10);

    return Number.isFinite(n) ? n : null;
};

const spanNum = (v: string): number => {
    if (!v.startsWith(SPAN_PREFIX)) {
        return 0;
    }

    const n = parseInt(v.slice(SPAN_PREFIX.length), 10);

    return Number.isFinite(n) ? n : 0;
};

const getOccupiedRowSpan = (style: CSSStyleDeclaration): number => {
    const rowSpan = spanNum(style.gridRowEnd);

    if (rowSpan > 0) {
        return rowSpan;
    }

    const start = lineNum(style.gridRowStart);
    const end = lineNum(style.gridRowEnd);

    if (start !== null && end !== null) {
        return Math.max(end - start, DEFAULT_ROW_SPAN);
    }

    return DEFAULT_ROW_SPAN;
};

const rowFromStyles = (gridItem: HTMLElement): number | null => {
    const style = getComputedStyle(gridItem);
    const start = lineNum(style.gridRowStart);

    if (start === null) {
        return null;
    }

    return start + getOccupiedRowSpan(style);
};

const relativeTopInGrid = (node: HTMLElement, gridRect: DOMRect): number => node.getBoundingClientRect().top - gridRect.top;

const rowFromOffset = (el: HTMLElement, grid: HTMLElement): number => {
    const gridRect = grid.getBoundingClientRect();
    const top = relativeTopInGrid(el, gridRect);
    const childTops = Array.from(grid.children)
        .filter(child => !isGridIframeHost(child))
        .map(child => {
            const childEl = child as HTMLElement;

            return {childEl, top: relativeTopInGrid(childEl, gridRect)};
        });
    const tops = Array.from(new Set(childTops.map(({top: childTop}) => childTop))).sort((a, b) => a - b);
    const rowIndex = tops.findIndex(t => Math.abs(t - top) <= POSITION_TOLERANCE_PX);

    if (rowIndex < 0) {
        return tops.length + 1;
    }

    const rowTop = tops[rowIndex];
    let maxRowEnd = 0;

    childTops.forEach(({childEl, top: childTop}) => {
        if (Math.abs(childTop - rowTop) > POSITION_TOLERANCE_PX) {
            return;
        }

        const rowEnd = rowFromStyles(childEl);

        if (rowEnd !== null) {
            maxRowEnd = Math.max(maxRowEnd, rowEnd);
        }
    });

    return maxRowEnd > 0 ? maxRowEnd : rowIndex + 1 + DEFAULT_ROW_SPAN;
};

const isMarketingBlock = (el: HTMLElement): boolean => el.matches(MARKETING_BLOCK_SELECTOR);

const isGridIframeHost = (el: Element): boolean => el instanceof HTMLElement && el.hasAttribute(CASINO_GAMES_IFRAME_ATTR);

const getMarketingGridChildren = (grid: HTMLElement): HTMLElement[] => Array.from(grid.children).filter(isMarketingBlock) as HTMLElement[];

const resolveGridItem = (el: HTMLElement, grid: HTMLElement): HTMLElement => {
    const marketingAncestor = el.closest(MARKETING_BLOCK_SELECTOR) as HTMLElement | null;

    if (marketingAncestor && grid.contains(marketingAncestor)) {
        return marketingAncestor;
    }

    let node: HTMLElement | null = el;

    while (node && node.parentElement !== grid) {
        node = node.parentElement;
    }

    return node ?? el;
};

const hasVerticalOverlap = (a: DOMRect, b: DOMRect): boolean => a.top < b.bottom - POSITION_TOLERANCE_PX && a.bottom > b.top + POSITION_TOLERANCE_PX;

const findMarketingOnSameRow = function(gameRect: DOMRect, grid: HTMLElement): HTMLElement[] {
    return getMarketingGridChildren(grid).filter(marketingEl => hasVerticalOverlap(gameRect, marketingEl.getBoundingClientRect()));
};

export const getIframeGridPlacement = (el: HTMLElement | null): IframeGridPlacement | null => {
    if (!el) {
        return DEFAULT_IFRAME_GRID_PLACEMENT;
    }

    const grid = el.closest(CASINO_GAMES_GRID_SELECTOR) as HTMLElement | null;

    if (!grid) {
        return DEFAULT_IFRAME_GRID_PLACEMENT;
    }

    const gridItem = resolveGridItem(el, grid);
    const isMarketing = isMarketingBlock(gridItem);
    const sameRowMarketing = isMarketing
        ? []
        : findMarketingOnSameRow(gridItem.getBoundingClientRect(), grid);
    const rowAnchor = isMarketing ? gridItem : sameRowMarketing[0] ?? gridItem;

    return {
        rowStart: rowFromStyles(rowAnchor) ?? rowFromOffset(rowAnchor, grid),
        skipShiftBlockKeys: sameRowMarketing
            .map(marketingEl => marketingEl.getAttribute(MARKETING_BLOCK_KEY_ATTR))
            .filter((key): key is string => Boolean(key))
    };
};
